/**
 * bronGroepen.js — een "bron" (leestekst, luisterfragment of videofragment)
 * hoort bij een groep vragen.
 *
 * Er is bewust geen aparte tabel: opeenvolgende vragen met dezelfde leestekst
 * (of dezelfde audio-url) vormen één groep. Zo werkte "Audio delen met
 * volgende" al; dit bestand maakt dezelfde regel expliciet voor tekst én audio
 * en wordt gebruikt door de editor (NieuweToetsPage), de shuffle
 * (shuffleUtils) en de afname (ToetsMaken).
 *
 * Werkt op zowel editor-objecten (audioUrl, maxAudioPlays, leestekst,
 * leestekstTitel) als afname-objecten (audioUrl, readingPassage,
 * readingPassageTitle) als databaserijen (audio_url, leestekst).
 *
 * Een bron_id (CE-vragenbank, migratie 077) gaat vóór audio en tekst: die
 * groep heeft een rijke bron (bronInhoud) en de leestekst is er de platte versie van.
 *
 * Videofragment (18-10-2026): een YouTube-link met begin- en eindtijd
 * (videoType 'youtube', zie youtubeFragment.js). Een losse geüploade video bij
 * één vraag (videoType leeg) is geen bron en vormt geen groep.
 *
 * Zelfde regels in backend/src/exam/bronGroepen.js: bij wijziging beide
 * bijwerken.
 */

const EDITOR_VELDEN = {
  audio: ['audioUrl', 'maxAudioPlays'],
  // videoTekst: wat er gezegd wordt, alleen in de wizard (voor AI-vragen), niet opgeslagen.
  video: ['videoUrl', 'videoType', 'videoTekst'],
  // bronId/bronInhoud: rijke bron uit de CE-vragenbank (migratie 077).
  // bronVelden: editorvelden van een eigen bron (bronvraag.js), alleen in de wizard.
  tekst: ['leestekst', 'leestekstTitel', 'bronId', 'bronInhoud', 'bronVelden'],
};

function bronIdVan(v) {
  return v.bronId ?? v.bron_id ?? null;
}

function audioVan(v) {
  return v.audioUrl ?? v.audio_url ?? null;
}

/** YouTube-link van een videofragment ('' = net toegevoegd, nog geen link), anders null. */
function videoVan(v) {
  if ((v.videoType ?? v.video_type) !== 'youtube') return null;
  return v.videoUrl ?? v.video_url ?? '';
}

function tekstVan(v) {
  const t = v.leestekst ?? v.readingPassage;
  return t === undefined ? null : t;
}

/**
 * Sleutel van de bron waar deze vraag bij hoort, of null.
 * Een bron_id (CE-vragenbank: tekst uit een tekstboekje of opgave-inleiding)
 * gaat voor alles; daarna audio, dan tekst. Een lege tekst ('') telt als bron: in de editor is
 * dat een net toegevoegde leestekst die nog ingevuld wordt.
 */
export function bronSleutel(v) {
  if (!v) return null;
  const bronId = bronIdVan(v);
  if (bronId) return 'bron:' + bronId;
  const audio = audioVan(v);
  if (audio) return 'audio:' + audio;
  const video = videoVan(v);
  if (video !== null) return 'video:' + video;
  const tekst = tekstVan(v);
  if (tekst !== null) return 'tekst:' + tekst;
  return null;
}

export function bronSoort(v) {
  const s = bronSleutel(v);
  if (!s) return null;
  if (s.startsWith('audio:')) return 'audio';
  if (s.startsWith('video:')) return 'video';
  return 'tekst';
}

/**
 * Verdeelt vragen in eenheden: opeenvolgende vragen met dezelfde sleutel
 * vormen één groep; een vraag zonder bron is een groep van één.
 * Geeft [{ sleutel, soort, van, tot, vragen }] met van/tot 1-gebaseerd.
 */
export function groepeerVragen(vragen) {
  const groepen = [];
  (vragen || []).forEach((v, i) => {
    const sleutel = bronSleutel(v);
    const vorige = groepen[groepen.length - 1];
    if (sleutel && vorige && vorige.sleutel === sleutel) {
      vorige.vragen.push(v);
      vorige.tot = i + 1;
    } else {
      groepen.push({ sleutel, soort: bronSoort(v), van: i + 1, tot: i + 1, vragen: [v] });
    }
  });
  return groepen;
}

/**
 * Zet vragen die bij dezelfde bron horen weer aaneengesloten, op de plek van
 * het eerste lid. De volgorde binnen een groep en de volgorde van losse
 * vragen blijven zoals ze waren. Aanroepen na elke herordening of toevoeging
 * in de editor; is idempotent.
 */
export function herstelGroepen(vragen) {
  const volgorde = [];
  const perSleutel = new Map();
  (vragen || []).forEach((v) => {
    const sleutel = bronSleutel(v);
    if (!sleutel) { volgorde.push([v]); return; }
    const bestaand = perSleutel.get(sleutel);
    if (bestaand) { bestaand.push(v); return; }
    const nieuw = [v];
    perSleutel.set(sleutel, nieuw);
    volgorde.push(nieuw);
  });
  return volgorde.flat();
}

/** Bronvelden (editor-namen) van een vraag, of null zonder bron. */
export function bronVanVraag(v) {
  const soort = bronSoort(v);
  if (!soort) return null;
  if (soort === 'audio') {
    return { soort, audioUrl: audioVan(v), maxAudioPlays: v.maxAudioPlays ?? v.max_audio_plays ?? null };
  }
  if (soort === 'video') return { soort, videoUrl: videoVan(v), videoType: 'youtube', videoTekst: v.videoTekst ?? '' };
  return {
    soort,
    leestekst: tekstVan(v) ?? '',
    leestekstTitel: v.leestekstTitel ?? v.readingPassageTitle ?? v.leestekst_titel ?? '',
    bronId: bronIdVan(v),
    bronInhoud: v.bronInhoud ?? v.bron_inhoud ?? null,
    bronVelden: v.bronVelden ?? null,
  };
}

function metBron(v, bron) {
  const velden = EDITOR_VELDEN[bron.soort];
  const kopie = { ...v };
  for (const veld of velden) kopie[veld] = bron[veld] ?? null;
  return kopie;
}

function zonderBron(v, soort) {
  const kopie = { ...v };
  for (const veld of EDITOR_VELDEN[soort]) kopie[veld] = null;
  return kopie;
}

/**
 * Past velden toe op álle vragen van de groep met deze sleutel (tekst
 * bewerken = de hele groep). Geeft de nieuwe array terug.
 */
export function pasBronToe(vragen, sleutel, velden) {
  return (vragen || []).map(v => (bronSleutel(v) === sleutel ? { ...v, ...velden } : v));
}

/**
 * Maakt de groep met deze sleutel precies n vragen groot: te weinig → de
 * volgende vragen na de groep krijgen dezelfde bron (hun eigen bron van die
 * soort vervalt), te veel → de laatste leden raken de bron kwijt. n < 1 wordt 1.
 */
export function zetGroepGrootte(vragen, sleutel, n) {
  const lijst = [...(vragen || [])];
  const groep = groepeerVragen(lijst).find(g => g.sleutel === sleutel);
  if (!groep) return lijst;
  const doel = Math.max(1, Math.floor(n));
  const huidig = groep.vragen.length;
  const start = groep.van - 1;
  if (doel > huidig) {
    const bron = bronVanVraag(groep.vragen[0]);
    const eind = Math.min(lijst.length, start + doel);
    for (let i = start + huidig; i < eind; i++) lijst[i] = metBron(lijst[i], bron);
  } else if (doel < huidig) {
    for (let i = start + doel; i < start + huidig; i++) lijst[i] = zonderBron(lijst[i], groep.soort);
  }
  return lijst;
}

/**
 * Voegt een nieuwe vraag direct achter de groep met deze sleutel in, met de
 * bronvelden van die groep. Bestaat de groep niet, dan achteraan zonder bron.
 */
export function voegVraagBijBron(vragen, sleutel, nieuweVraag) {
  const lijst = [...(vragen || [])];
  const groep = groepeerVragen(lijst).find(g => g.sleutel === sleutel);
  if (!groep) return [...lijst, nieuweVraag];
  const bron = bronVanVraag(groep.vragen[0]);
  lijst.splice(groep.tot, 0, metBron(nieuweVraag, bron));
  return lijst;
}

/** Haalt de bron van alle vragen in de groep weg (groep opheffen). */
export function verwijderBron(vragen, sleutel) {
  const soort = !sleutel ? 'tekst' : sleutel.startsWith('audio:') ? 'audio' : sleutel.startsWith('video:') ? 'video' : 'tekst';
  return (vragen || []).map(v => (bronSleutel(v) === sleutel ? zonderBron(v, soort) : v));
}
